import React, { memo, useEffect, useRef, useState } from "react";
import { Box, Stack, Typography, Button } from "@mui/material";
import Slider from "react-slick";
import {
  EastOutlined,
  WestOutlined,
  LockOutlined,
  AddAPhotoOutlined,
  SatelliteAltOutlined,
  StreetviewOutlined,
} from "@mui/icons-material";
import type { GoogleSlide } from "../../../../Hooks/useGoogleSlides";
import BlurredMlsPlaceholder from "../../../../Components/BlurredMlsPlaceholder/BlurredMlsPlaceholder";

const GALLERY_HEIGHT = {
  xl: "450px",
  lg: "425px",
  md: "400px",
  sm: "375px",
  xs: "350px",
};

// "Last updated: X days ago" from the most recent owner-photo upload time.
const daysAgoLabel = (iso?: string): string | null => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (isNaN(then)) return null;
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
};

const PropertyPhotoGallery = memo((props: any) => {
  const [sliderIndex, setSliderIndex] = useState(0);
  const sliderRef: any = useRef(null);

  // Google-derived slides (Satellite + optional Street View). Always lead
  // the slider so even an unclaimed property with zero owner uploads
  // still has a useful gallery instead of the empty placeholder.
  const googleSlides: GoogleSlide[] = props?.googleSlides || [];
  // Owner-uploaded photos follow the Google slides.
  const ownerPhotos: any[] = props?.ownerPhotos || [];
  const hasOwnerPhotos = ownerPhotos.length > 0;
  const isClaimed = Boolean(props?.isClaimed);
  // The current user owns this property → can add/manage photos.
  const canManage = Boolean(props?.canManage);
  const onManage: (() => void) | undefined = props?.onManage;

  // Combined slide list. Owner photos LEAD (per client request 2026-07-01)
  // — the homeowner's actual photos are the strongest signal about the
  // property; the map-derived Satellite / Street View slides act as
  // supplementary context when curiosity extends beyond the owner's
  // curation. For unclaimed properties with no owner photos the Google
  // slides land first by default (nothing precedes them).
  type GallerySlide =
    | { kind: "google"; google: GoogleSlide }
    | { kind: "owner"; owner: any; index: number };
  const slides: GallerySlide[] = [
    ...ownerPhotos.map((owner, index) => ({
      kind: "owner" as const,
      owner,
      index,
    })),
    ...googleSlides.map((g) => ({ kind: "google" as const, google: g })),
  ];
  const hasSlides = slides.length > 0;

  const lastUpdated = hasOwnerPhotos
    ? daysAgoLabel(
        ownerPhotos
          .map((p) => p?.uploadedAt)
          .filter(Boolean)
          .sort()
          .pop()
      )
    : null;

  const settings = {
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
    swipeToSlide: true,
    responsive: [],
    touchThreshold: 100,
    // Auto-advance every 5s when there's more than one slide. Pauses on
    // hover so a visitor reading photo detail isn't jerked to the next
    // slide mid-read. Only enabled for slides.length > 1 to avoid the
    // slider recomputing every 5s for a single-slide gallery.
    autoplay: slides.length > 1,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    afterChange: (current: any) => setSliderIndex(current),
  };

  const slideRight = () => sliderRef?.current?.slickNext();
  const slideLeft = () => sliderRef?.current?.slickPrev();

  // If the slide count shrinks (e.g., Street View metadata flips from OK
  // to ZERO_RESULTS on a coord change), the cached index may now point
  // past the end. Snap back to the first slide so the counter and slider
  // stay consistent.
  useEffect(() => {
    if (sliderIndex >= slides.length && slides.length > 0) {
      sliderRef?.current?.slickGoTo(0);
      setSliderIndex(0);
    }
  }, [slides.length, sliderIndex]);

  // ---- Empty state: no slides at all → single blurred placeholder.
  // Reached only if the property has no valid coords AND no owner photos.
  // Source preference: a random MLS photo (blurred) over the branded
  // webp fallback. Lock when the property is not yet claimed (same
  // behaviour as the cards).
  if (!hasSlides) {
    return (
      <Box position="relative" sx={{ height: GALLERY_HEIGHT }}>
        <BlurredMlsPlaceholder
          mls={props?.mls}
          seed={props?.propertyId ?? ""}
          borderRadius={20}
        />
        {canManage ? (
          // Owner with no photos yet → prompt to add their first photos.
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0,0,0,0.18)",
            }}
          >
            <Button
              onClick={onManage}
              variant="contained"
              startIcon={<AddAPhotoOutlined />}
              sx={{
                bgcolor: "primary.main",
                color: "#fff",
                borderRadius: 2,
                px: 3,
                py: 1.2,
                fontWeight: 600,
                boxShadow: 4,
              }}
            >
              Add photos
            </Button>
          </Box>
        ) : (
          !isClaimed && (
            <Box
              aria-label="Unclaimed property"
              sx={{
                position: "absolute",
                top: 18,
                right: 18,
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: "primary.main",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: 3,
              }}
            >
              <LockOutlined sx={{ fontSize: 28 }} />
            </Box>
          )
        )}
      </Box>
    );
  }

  // ---- Combined Google + owner-photo slider
  return (
    <Box>
      <Box position={"relative"}>
        <Slider ref={sliderRef} {...settings} arrows={false}>
          {slides.map((s, i) => {
            const isGoogle = s.kind === "google";
            const url = isGoogle ? s.google.url : s.owner?.url;
            // TS narrows `s` to the owner variant in the else branch.
            const alt = isGoogle
              ? s.google.label
              : `Property photo ${s.index + 1}`;
            const slideKey = isGoogle
              ? `google-${s.google.type}`
              : s.owner?._id || `owner-${i}`;

            return (
              <Box
                key={slideKey}
                borderRadius={4}
                overflow={"hidden"}
                border={"none"}
                height={GALLERY_HEIGHT}
                position={"relative"}
              >
                <img
                  width={"100%"}
                  height={"100%"}
                  style={{ borderRadius: 20, objectFit: "cover" }}
                  src={url}
                  alt={alt}
                />
                {isGoogle && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 16,
                      left: 16,
                      bgcolor: "rgba(0, 0, 0, 0.62)",
                      color: "#fff",
                      px: 1.25,
                      py: 0.5,
                      borderRadius: 1,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                    }}
                    data-testid={`google-slide-label-${s.google.type}`}
                  >
                    {s.google.type === "satellite" ? (
                      <SatelliteAltOutlined sx={{ fontSize: 16 }} />
                    ) : (
                      <StreetviewOutlined sx={{ fontSize: 16 }} />
                    )}
                    <Typography variant="caption" fontWeight={600}>
                      {s.google.label}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Slider>

        {slides.length > 1 && (
          <Box
            position={"absolute"}
            right={0}
            height={"100%"}
            top={0}
            alignItems={"center"}
            sx={{ cursor: "pointer" }}
            justifyContent={"center"}
            display={{ xs: "none", sm: "none", md: "flex", lg: "flex", xl: "flex" }}
          >
            <Box
              p={"20px"}
              borderRadius={10}
              bgcolor={"white"}
              height={5}
              width={5}
              alignItems={"center"}
              justifyContent={"center"}
              sx={{ cursor: "pointer", backdropFilter: "blur(5px)", boxShadow: 5 }}
              onClick={slideRight}
              display={"flex"}
            >
              <EastOutlined fontSize="inherit" sx={{ fontSize: "15px" }} />
            </Box>
          </Box>
        )}

        {slides.length > 1 && (
          <Box
            position={"absolute"}
            left={0}
            height={"100%"}
            top={0}
            alignItems={"center"}
            sx={{ cursor: "pointer" }}
            justifyContent={"center"}
            display={{ xs: "none", sm: "none", md: "flex", lg: "flex", xl: "flex" }}
          >
            <Box
              p={"20px"}
              borderRadius={10}
              bgcolor={"white"}
              height={5}
              width={5}
              alignItems={"center"}
              justifyContent={"center"}
              sx={{ cursor: "pointer", backdropFilter: "blur(5px)", boxShadow: 5 }}
              onClick={slideLeft}
              display={"flex"}
            >
              <WestOutlined fontSize="inherit" sx={{ fontSize: "15px" }} />
            </Box>
          </Box>
        )}

        {/* photo count + owner manage button */}
        <Box bottom={30} position={"absolute"} right={"5%"}>
          <Stack direction={"row"} spacing={1.5} display={"flex"} alignItems={"center"}>
            {canManage && !props?.hideManageButton && (
              <Button
                onClick={onManage}
                size="small"
                startIcon={<AddAPhotoOutlined sx={{ fontSize: 16 }} />}
                sx={{
                  bgcolor: "white",
                  color: "primary.main",
                  fontWeight: 600,
                  borderRadius: 1,
                  px: 1.5,
                  boxShadow: 2,
                  "&:hover": { bgcolor: "#f3f3f3" },
                }}
              >
                Manage
              </Button>
            )}
            <Box px={2} py={1} bgcolor={"white"} borderRadius={1}>
              <Typography component={"span"} variant="body2" fontWeight={500}>
                {`${Math.min(sliderIndex, slides.length - 1) + 1} / ${slides.length}`}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* last updated (from the most recent owner-photo upload) */}
        {lastUpdated && (
          <Box
            bgcolor={"rgba(0, 0, 0, .4)"}
            position={"absolute"}
            top={"5%"}
            right={"5%"}
            px={3}
            py={0.7}
            borderRadius={50}
          >
            <Typography variant="inherit" sx={{ fontSize: 12 }} color="white">
              Last updated: {lastUpdated}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
});

export default PropertyPhotoGallery;
